import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Modal } from "shared/components";
import { Button, ButtonVariant } from "shared/components";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components";
import { Input } from "shared/components/forms/Input";
import { Select } from "shared/components/forms/Select";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";

interface AdditionalTemplate {
  id: number;
  name: string;
  templateIdentifier: string;
}

interface AddTemplateModalProps {
  opened: boolean;
  onClose: () => void;
  onSave: (template: { name: string; templateIdentifier: string }) => void;
  templateOptions: Array<{ value: string; label: string }>;
  existingTemplates: AdditionalTemplate[];
  isDisabled?: boolean;
  isSaving?: boolean;
}

export const AddTemplateModal = ({
  opened,
  onClose,
  onSave,
  templateOptions,
  existingTemplates,
  isDisabled,
  isSaving
}: AddTemplateModalProps) => {
  const [templateName, setTemplateName] = useState("");
  const [templateIdentifier, setTemplateIdentifier] = useState("");

  useEffect(() => {
    if (!opened) {
      setTemplateName("");
      setTemplateIdentifier("");
    }
  }, [opened]);

  const handleSave = () => {
    const trimmedName = templateName.trim();
    const trimmedIdentifier = templateIdentifier.trim();

    if (!trimmedName || !trimmedIdentifier) {
      toast.error("Please fill in both name and template fields");
      return;
    }

    if (
      existingTemplates.some(
        (t) => t.name.toLowerCase() === trimmedName.toLowerCase()
      )
    ) {
      toast.error("A template with this name already exists");
      return;
    }

    if (
      existingTemplates.some((t) => t.templateIdentifier === trimmedIdentifier)
    ) {
      toast.error("This template has already been added");
      return;
    }

    onSave({ name: trimmedName, templateIdentifier: trimmedIdentifier });
  };

  const handleClose = () => {
    setTemplateName("");
    setTemplateIdentifier("");
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      panelClassName="w-full max-w-[477px] overflow-auto rounded-lg"
    >
      <div className="border-b border-alpha-100 px-6 py-4">
        <div className="flex items-start justify-between gap-1">
          <h3 className="text-xl font-bold text-content-1000">
            Add additional template
          </h3>
          <IconButton
            size={IconButtonSize.LG}
            variant={IconButtonVariant.TEXT_BASE}
            HugeIcon={Cancel01Icon}
            onClick={handleClose}
            className="text-content-400 hover:text-content-500"
          />
        </div>
      </div>

      <div className="bg-background-900 p-6 space-y-6">
        <div>
          <Input
            label="Name"
            id="new_template_name"
            placeholder="Add name"
            legacyErrorMargins={false}
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            className="w-full"
            required
          />
        </div>
        <div>
          <Select
            id="new-template-select"
            label="Template"
            value={templateIdentifier}
            onChange={(e) => setTemplateIdentifier(e.target.value)}
            options={templateOptions}
            legacyErrorMargins={false}
            placeholder="Select template"
            className="w-full"
            isDisabled={isDisabled}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            required
          />
        </div>
      </div>

      <div className="border-t border-alpha-100 px-6 py-4 flex items-center justify-end gap-2">
        <Button
          variant={ButtonVariant.BASE}
          onClick={handleClose}
          className="max-w-[125px] w-full"
        >
          Cancel
        </Button>
        <Button
          variant={ButtonVariant.PRIMARY}
          onClick={handleSave}
          className="w-[136px]"
          disabled={isSaving || isDisabled}
        >
          {isSaving ? "Adding..." : "Add template"}
        </Button>
      </div>
    </Modal>
  );
};
